import React from "react";
import * as THREE from "three";
import { SymbolType } from "../../../../../types/floorplannerTypes";
import SelectedMeshOutline from "../../../scene/SelectedMeshOutline";
import { DoorHandleStyle } from "./door3DStyle";
import DoorSlotMaterial from "./DoorSlotMaterial";

type OpacityMaterialProps = {
  opacity: number;
  transparent: boolean;
  depthWrite: boolean;
};

type DoorHandle3DProps = {
  symbol: SymbolType;
  style: DoorHandleStyle;
  handleCenter: THREE.Vector3 | { x: number; y: number; z: number };
  leafWidth: number;
  leafThickness: number;
  leafHeight: number;
  /** Bottom clearance used for lock cylinder placement on lever sets. */
  bottomGap?: number;
  /**
   * Face normal sign along leaf Y (+1 / -1). When omitted, derived from handleCenter.y.
   * Sliding leaves often pass an explicit face side because the leaf sits on the wall face.
   */
  faceSide?: number;
  /** Lever reaches along ±X (hinged doors). Ignored for knob/bar. */
  leverDirection?: number;
  /**
   * Sliding solid/glass leaves live in a group with Y already on the wall face,
   * so handle local Y should be measured from thickness/2 rather than handleCenter.y.
   */
  useThicknessFace?: boolean;
  /** Fallback when no Ready material is assigned to doorHardware (beslag). */
  handleColor: THREE.Color;
  keyholeColor?: THREE.Color;
  opacityMaterialProps: OpacityMaterialProps;
  selected: boolean;
};

const HandleMaterial: React.FC<{
  symbol: SymbolType;
  fallbackColor: THREE.ColorRepresentation;
  opacityMaterialProps: OpacityMaterialProps;
  roughness?: number;
  metalness?: number;
}> = ({
  symbol,
  fallbackColor,
  opacityMaterialProps,
  roughness = 0.22,
  metalness = 0.72,
}) => (
  <DoorSlotMaterial
    symbol={symbol}
    slot="doorHardware"
    fallbackColor={fallbackColor}
    fallbackRoughness={roughness}
    fallbackMetalness={metalness}
    {...opacityMaterialProps}
  />
);

/**
 * Procedural door hardware: lever (default), knob, pull bar, or none.
 */
const DoorHandle3D: React.FC<DoorHandle3DProps> = ({
  symbol,
  style,
  handleCenter,
  leafWidth,
  leafThickness,
  leafHeight,
  bottomGap = 0,
  faceSide,
  leverDirection = -1,
  useThicknessFace = false,
  handleColor,
  keyholeColor = new THREE.Color(0x2f3337),
  opacityMaterialProps,
  selected,
}) => {
  if (style === "none") return null;

  // Prefer explicit faceSide; otherwise put hardware on the leaf face matching handleCenter.y.
  const side = faceSide ?? (handleCenter.y >= 0 ? 1 : -1);
  const hx = handleCenter.x;
  const hz = handleCenter.z;
  const baseY = useThicknessFace ? 0 : handleCenter.y;

  if (style === "bar") {
    const barRadius = 0.012;
    const barLength = Math.min(Math.max(leafHeight * 0.14, 0.22), 0.36);
    const standoffDepth = 0.035;
    const faceY = useThicknessFace
      ? side * (leafThickness / 2 + standoffDepth)
      : baseY +
        side *
          (Math.abs(baseY) > 1e-6
            ? standoffDepth * 0.5
            : leafThickness / 2 + standoffDepth);
    const mountY = useThicknessFace
      ? side * (leafThickness / 2 + standoffDepth / 2)
      : baseY + side * (standoffDepth / 2);

    return (
      <group>
        <mesh
          position={[hx, faceY, hz]}
          rotation={[Math.PI / 2, 0, 0]}
          castShadow
          receiveShadow
        >
          <cylinderGeometry args={[barRadius, barRadius, barLength, 20]} />
          <HandleMaterial
            symbol={symbol}
            fallbackColor={handleColor}
            opacityMaterialProps={opacityMaterialProps}
            roughness={0.18}
            metalness={0.82}
          />
          <SelectedMeshOutline visible={selected} />
        </mesh>
        <mesh
          position={[hx, mountY, hz + barLength * 0.38]}
          rotation={[Math.PI / 2, 0, 0]}
          castShadow
          receiveShadow
        >
          <cylinderGeometry
            args={[barRadius * 0.9, barRadius * 0.9, standoffDepth, 16]}
          />
          <HandleMaterial
            symbol={symbol}
            fallbackColor={handleColor}
            opacityMaterialProps={opacityMaterialProps}
            roughness={0.18}
            metalness={0.82}
          />
        </mesh>
        <mesh
          position={[hx, mountY, hz - barLength * 0.38]}
          rotation={[Math.PI / 2, 0, 0]}
          castShadow
          receiveShadow
        >
          <cylinderGeometry
            args={[barRadius * 0.9, barRadius * 0.9, standoffDepth, 16]}
          />
          <HandleMaterial
            symbol={symbol}
            fallbackColor={handleColor}
            opacityMaterialProps={opacityMaterialProps}
            roughness={0.18}
            metalness={0.82}
          />
        </mesh>
      </group>
    );
  }

  const rosetteRadius = Math.max(leafThickness * 0.72, 0.038);
  const rosetteDepth = Math.max(leafThickness * 0.22, 0.01);
  const hardwareY = useThicknessFace
    ? side * (leafThickness / 2 + rosetteDepth / 2)
    : baseY + side * rosetteDepth * 0.2;

  if (style === "knob") {
    const knobRadius = Math.max(leafThickness * 0.55, 0.028);
    const neckLength = Math.max(rosetteDepth * 0.9, 0.012);
    const knobY =
      hardwareY +
      side * (rosetteDepth / 2 + neckLength / 2 + knobRadius * 0.35);

    return (
      <group>
        <mesh position={[hx, hardwareY, hz]} castShadow receiveShadow>
          <cylinderGeometry
            args={[rosetteRadius, rosetteRadius, rosetteDepth, 32]}
          />
          <HandleMaterial
            symbol={symbol}
            fallbackColor={handleColor}
            opacityMaterialProps={opacityMaterialProps}
          />
          <SelectedMeshOutline visible={selected} />
        </mesh>
        <mesh
          position={[
            hx,
            hardwareY + side * (rosetteDepth / 2 + neckLength / 2),
            hz,
          ]}
          castShadow
          receiveShadow
        >
          <cylinderGeometry
            args={[knobRadius * 0.35, knobRadius * 0.35, neckLength, 16]}
          />
          <HandleMaterial
            symbol={symbol}
            fallbackColor={handleColor}
            opacityMaterialProps={opacityMaterialProps}
            roughness={0.2}
            metalness={0.76}
          />
        </mesh>
        <mesh position={[hx, knobY, hz]} castShadow receiveShadow>
          <sphereGeometry args={[knobRadius, 24, 16]} />
          <HandleMaterial
            symbol={symbol}
            fallbackColor={handleColor}
            opacityMaterialProps={opacityMaterialProps}
            roughness={0.18}
            metalness={0.8}
          />
          <SelectedMeshOutline visible={selected} />
        </mesh>
      </group>
    );
  }

  // Lever (default hinged hardware)
  const leverLength = Math.min(Math.max(leafWidth * 0.16, 0.11), 0.16);
  const leverThickness = Math.max(leafThickness * 0.34, 0.018);
  const lockZ = Math.max(bottomGap + 0.32, hz - 0.19);
  const raisedY = hardwareY + side * (rosetteDepth / 2 + leverThickness / 2);

  return (
    <group>
      <mesh position={[hx, hardwareY, hz]} castShadow receiveShadow>
        <cylinderGeometry
          args={[rosetteRadius, rosetteRadius, rosetteDepth, 32]}
        />
        <HandleMaterial
          symbol={symbol}
          fallbackColor={handleColor}
          opacityMaterialProps={opacityMaterialProps}
        />
        <SelectedMeshOutline visible={selected} />
      </mesh>
      <mesh
        position={[hx + leverDirection * leverLength * 0.08, raisedY, hz]}
        castShadow
        receiveShadow
      >
        <boxGeometry
          args={[
            Math.max(leverThickness * 1.35, 0.03),
            leverThickness,
            leverThickness,
          ]}
        />
        <HandleMaterial
          symbol={symbol}
          fallbackColor={handleColor}
          opacityMaterialProps={opacityMaterialProps}
          roughness={0.2}
          metalness={0.76}
        />
        <SelectedMeshOutline visible={selected} />
      </mesh>
      <mesh
        position={[hx + leverDirection * leverLength * 0.55, raisedY, hz]}
        castShadow
        receiveShadow
      >
        <boxGeometry
          args={[leverLength, leverThickness, leverThickness * 0.78]}
        />
        <HandleMaterial
          symbol={symbol}
          fallbackColor={handleColor}
          opacityMaterialProps={opacityMaterialProps}
          roughness={0.2}
          metalness={0.76}
        />
        <SelectedMeshOutline visible={selected} />
      </mesh>
      <mesh position={[hx, hardwareY, lockZ]} castShadow receiveShadow>
        <cylinderGeometry
          args={[
            rosetteRadius * 0.72,
            rosetteRadius * 0.72,
            rosetteDepth,
            28,
          ]}
        />
        <HandleMaterial
          symbol={symbol}
          fallbackColor={handleColor}
          opacityMaterialProps={opacityMaterialProps}
          roughness={0.24}
          metalness={0.72}
        />
        <SelectedMeshOutline visible={selected} />
      </mesh>
      <mesh
        position={[
          hx,
          hardwareY + side * (rosetteDepth / 2 + 0.002),
          lockZ - rosetteRadius * 0.05,
        ]}
        castShadow
      >
        <boxGeometry
          args={[rosetteRadius * 0.24, 0.004, rosetteRadius * 0.72]}
        />
        <meshStandardMaterial
          color={keyholeColor}
          roughness={0.5}
          metalness={0.1}
          {...opacityMaterialProps}
        />
      </mesh>
    </group>
  );
};

export default DoorHandle3D;
